import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Popover from "@mui/material/Popover";
import { theme } from "@polifonia/theme";
import type { ColorResult } from "@uiw/react-color";
import { Sketch } from "@uiw/react-color";
import { useMemo, useState } from "react";

import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import { defineStyles } from "@/internal/emotion";
import { getTextColorFromBackgroundColor } from "@/lib/color";

// Utility to convert rgba to 8-digit hex (#RRGGBBAA)
const rgbaToHex8 = (r: number, g: number, b: number, a: number = 1): string => {
  const toHex = (x: number) => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };
  const alpha = Math.round(a * 255);
  return (
    "#" + toHex(r) + toHex(g) + toHex(b) + (alpha < 255 ? toHex(alpha) : "")
  );
};

// Utility to parse rgba color string to object
const parseColorString = (color: string) => {
  if (typeof color === "string" && color.startsWith("rgba")) {
    const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
    if (match) {
      const [, rStr, gStr, bStr, aStr] = match;
      const r = Number(rStr) / 255;
      const g = Number(gStr) / 255;
      const b = Number(bStr) / 255;
      const a = Number(aStr);

      // RGB to HSV conversion
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const delta = max - min;
      let h = 0;
      let s = 0;
      const v = max;

      if (delta !== 0) {
        if (max === r) {
          h = 60 * (((g - b) / delta) % 6);
        } else if (max === g) {
          h = 60 * ((b - r) / delta + 2);
        } else {
          h = 60 * ((r - g) / delta + 4);
        }
        if (h < 0) h += 360;
        s = delta / max;
      }

      return { h, s, v, a };
    }
  }
  return color;
};

const styles = defineStyles({
  container: css({
    position: "relative",
    width: "32px",
  }),
  swatch: css({
    width: "32px",
    height: "20px",
    border: `1px solid ${theme.colors.neutral030}`,
    borderRadius: "4px",
    cursor: "pointer",
  }),
  swatchDisabled: css({
    cursor: "not-allowed",
    backgroundColor: theme.colors.neutral010,
  }),
  text: css({
    position: "absolute",
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  }),
  clearIcon: css({
    position: "absolute",
    top: "50%",
    right: 8,
    fontSize: 16,
    cursor: "pointer",
    transform: "translateY(-50%)",
  }),
});

namespace ColorPicker {
  export interface Props {
    /** hex color string or rgba color string */
    color: string;
    onColorChange: (color: string) => void;
    disabled?: boolean;
    width?: number | string;
    height?: number | string;
    pickerProps?: React.ComponentProps<typeof Sketch>;
    /** Display the hex color value as text overlay */
    showColorText?: boolean;
    /** Show clear button to reset color */
    allowClear?: boolean;
    /** Placeholder for the color picker */
    placeholder?: string;
    /** Default color to reset to when clear button is clicked. If not provided, defaults to empty string */
    defaultColor?: string;
  }
}

export const ColorPicker: React.FC<ColorPicker.Props> = function ColorPicker({
  color,
  onColorChange,
  disabled = false,
  width = "32px",
  height = "20px",
  pickerProps,
  showColorText = false,
  allowClear = false,
  placeholder = "",
  defaultColor = "",
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const parsedColor = useMemo(() => {
    return parseColorString(color);
  }, [color]);

  const handleOpen = useHandler((event: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  });

  const handleClose = useHandler(() => {
    setAnchorEl(null);
  });

  const handleClear = useHandler((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onColorChange(defaultColor);
  });

  const handleColorChange = useHandler((colorResult: ColorResult) => {
    const { r, g, b, a } = colorResult.rgba;
    onColorChange(rgbaToHex8(r, g, b, a));
  });

  const textColor = useMemo(() => {
    if (!color) return theme.colors.neutral070;
    return getTextColorFromBackgroundColor(color);
  }, [color]);

  const open = Boolean(anchorEl);

  return (
    <>
      <div
        css={[
          styles.container,
          css({
            width,
          }),
        ]}
      >
        <div
          css={[
            styles.swatch,
            css({
              width,
              height,
              backgroundColor: color,
            }),
            disabled && styles.swatchDisabled,
          ]}
          onClick={handleOpen}
        />
        {showColorText && (color || placeholder) ? (
          <Typography
            variant="body"
            css={[
              styles.text,
              css({
                left: allowClear ? 32 : 0,
                right: allowClear ? 32 : 0,
                color: textColor,
              }),
            ]}
          >
            {color.toUpperCase() || placeholder}
          </Typography>
        ) : null}
        {allowClear && color ? (
          <MotifIcon
            un-i-motif="cross"
            css={[
              styles.clearIcon,
              css({
                color: showColorText ? textColor : theme.colors.neutral070,
              }),
            ]}
            onClick={disabled ? undefined : handleClear}
          />
        ) : null}
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 0,
            },
          },
        }}
      >
        <Sketch
          color={parsedColor}
          onChange={handleColorChange}
          {...pickerProps}
        />
      </Popover>
    </>
  );
};
